<template>
  <div :class="['text-field-container', { label: !field.value, disabled }]">
    <div class="text-field">
      <div v-if="showInput" class="input-wrapper">
        <input
          ref="inputRef"
          v-model="inputValue"
          class="text-field-input"
          :disabled="disabled"
          :autofocus="fieldFocused"
          :placeholder="placeholder"
          @input="debounceHandleInput(($event.target as HTMLInputElement).value)"
          @focus="handleFocus"
          @blur="handleBlur"
        />
        <span v-if="showRequired && !inputValue" class="required-star">*</span>
      </div>
      <div
        v-else
        :class="['text-field-value-container', { required: showRequired }]"
        @click="handleFocus"
      >
        {{ value }}
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { useDebounceFn } from '@vueuse/core';
import type { PropType } from 'vue';
import { defineComponent, watch, computed, ref, onMounted } from 'vue';
import { t } from '../../helpers/translation.helper';
import type { Field } from '../types/field';
import { FieldSource } from '../types/enums/field-source';

export default defineComponent({
  props: {
    field: {
      type: Object as PropType<Field>,
      required: true,
    },
    disabled: {
      type: Boolean,
    },
    completed: {
      type: Boolean,
    },
    fieldFocused: {
      type: Boolean,
    },
    isConnectedToCurrentRecipient: {
      type: Boolean,
    },
  },
  emits: ['update', 'focus', 'blur'],
  setup(props, { emit }) {
    const focused = ref(false);
    const inputValue = ref('');
    const inputRef = ref<HTMLInputElement>(null);

    const handleInput = (value: string) => {
      if (props.disabled) {
        return;
      }
      emit('update', value);
    };

    const debounceHandleInput = useDebounceFn(handleInput, 300);

    const watchFieldFocused = (fieldFocused: boolean) => {
      if (fieldFocused) {
        const input = inputRef.value;
        input.focus({
          preventScroll: true,
        });
      }
    };

    watch(() => props.fieldFocused, watchFieldFocused);

    const showInput = computed(() => props.isConnectedToCurrentRecipient);

    const showRequired = computed(() => {
      if (focused.value || props.completed || props.disabled) {
        return false;
      }
      return props.field.isRequired;
    });

    const value = computed(() => {
      if (props.field.source === FieldSource.Sender) {
        return props.field.value;
      }
      return props.field.value || props.field.label || t('enter-text-here');
    });

    const placeholder = computed(() => props.field.label || t('enter-text-here'));

    const handleFocus = () => {
      focused.value = true;
      emit('focus');
    };

    const handleBlur = () => {
      focused.value = false;
      emit('blur');
    };

    onMounted(() => {
      inputValue.value = props.field.value;
    });

    return {
      focused,
      inputRef,
      inputValue,
      showInput,
      showRequired,
      value,
      placeholder,
      handleInput,
      watchFieldFocused,
      handleFocus,
      handleBlur,
      debounceHandleInput,
    };
  },
});
</script>
<style lang="scss" scoped>
.text-field-container {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: row;
  overflow: hidden;

  &.label:not(.locked) {
    color: var(--text-gray);
  }

  &.disabled {
    pointer-events: none;
    opacity: 1;
  }

  .text-field {
    height: 100%;
    width: 100%;
    overflow: hidden;

    .text-field-input {
      height: 100%;
      width: 100%;
      border: none;
      background: none;
      outline: none;
      box-shadow: none;
      margin: 0;
      padding: 0;
      font-size: 1em;
      color: var(--text-dark);

      &:focus {
        cursor: text;
      }
    }
  }

  .input-wrapper {
    display: flex;
  }

  .required-star {
    color: var(--error-dark);
    line-height: normal;
  }

  .text-field-value-container {
    height: 100%;
    width: 100%;
    overflow: hidden;
    font-size: 1em;
    line-height: 1.7;
    font-weight: normal;
    text-overflow: ellipsis;

    &.required::after {
      color: var(--error-dark);
      content: '*';
      margin-left: 2px;
    }
  }
}
</style>
